import { useEffect, useMemo, useRef, useState } from "react";
import {
    Card, Chip, CircularProgress, Grid, Icon, InputLabel, Tab, Tabs,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { get, post, postUpload, postUploadFile } from "utils/WebService";
import { errorAlert } from "utils/Functions";
import { toCurrency } from "utils/NumberUtils";
import { useMaterialUIController } from "context";
import { CLARO, ESCURO, coresDoTema, corCiano, corRoxo, TEXTO_COLORIDO_ESCURO } from "assets/coresTema";

// Arquivo mais recente × anterior e × base de dados, lidos pelo modelo de remessa como a importação, e a radiografia
// de uma importação já feita (o que ela fez com cada ficha, gravado por ela) (ComparativoBaseService). Só leitura.
const CATEGORIAS_ARQUIVOS = [
    { key: "ENTROU", label: "Entraram", cor: "success", campo: "entraram", dica: "Devedores que estão no arquivo mais recente e não estavam no anterior" },
    { key: "SAIU", label: "Saíram", cor: "error", campo: "sairam", dica: "Devedores que estavam no arquivo anterior e não vieram no mais recente" },
    { key: "MANTEVE", label: "Mantidos", cor: "info", campo: "mantidos", dica: "Devedores que estão nos dois arquivos (a observação diz o que mudou nos contratos)" },
];

const CATEGORIAS_BASE = [
    { key: "MANTEM", label: "Mantidos", cor: "info", campo: "mantidos", dica: "Fichas ativas na carteira que vieram de novo no arquivo" },
    { key: "ENTRA", label: "Casos novos", cor: "success", campo: "entram", dica: "Devedores sem ficha na carteira: a importação cria a ficha (e o devedor, se for novo no cliente)" },
    { key: "REATIVA", label: "Reativados", cor: "warning", campo: "reativados", dica: "Fichas devolvidas que voltam para Cobrança: o arquivo traz débito em aberto" },
    { key: "QUITADO_VOLTA", label: "Quitados que voltaram", cor: "roxo", campo: "quitadosVoltam", dica: "Fichas quitadas que voltam para Cobrança: o arquivo traz débito em aberto (contrato novo ou com outro valor)" },
    { key: "NAO_REATIVA", label: "Não reativam", cor: "secondary", campo: "naoReativam", dica: "Fichas devolvidas ou quitadas que vieram no arquivo só com contratos quitados com o mesmo valor ou em acordo ativo: continuam como estão" },
    { key: "SAI", label: "Saem", cor: "error", campo: "saem", dica: "Fichas ativas cujo devedor não veio no arquivo: o batimento devolve" },
    { key: "FICA_ACORDO", label: "Ficam por acordo", cor: "ciano", campo: "ficamPorAcordo", dica: "Fichas ativas cujo devedor não veio no arquivo, mas que têm acordo ativo: o batimento não devolve" },
    { key: "ERRO", label: "Com erro", cor: "error", campo: "erros", dica: "Devedores que a importação não gravou (a observação traz o erro)", soComValor: true },
];

const LIMITE_TELA = 500;

const texto = (v) => (v === null || v === undefined || v === "" ? "-" : v);

// Cores fora da paleta do MUI (roxo = quitada, como no resto do sistema; ciano = acordo), de assets/coresTema
const CORES_PROPRIAS = { roxo: corRoxo, ciano: corCiano };

// Cor dos cards: a barra na cor do tema; número e ícone no escuro com o tom claro (a cor "main" some no cinza)
const corDaBarra = (cor, darkMode) => (CORES_PROPRIAS[cor] ? CORES_PROPRIAS[cor](darkMode) : `${cor}.main`);
const corDoTexto = (cor, darkMode) => {
    if (CORES_PROPRIAS[cor]) return CORES_PROPRIAS[cor](darkMode);
    return darkMode ? TEXTO_COLORIDO_ESCURO[cor] : `${cor}.main`;
};

/** Etiqueta (Chip) na cor da categoria; cor fora da paleta do MUI vai pelo sx. */
const corDaEtiqueta = (cor) => (CORES_PROPRIAS[cor] ? "default" : cor);
const sxDaEtiqueta = (cor, darkMode, cheia) => {
    if (!CORES_PROPRIAS[cor]) return {};
    const c = CORES_PROPRIAS[cor](darkMode);
    return cheia
        ? { bgcolor: c, color: darkMode ? ESCURO.grafico : CLARO.cartao, borderColor: c }
        : { color: c, borderColor: c };
};

/** Card de uma categoria (clicável: filtra a lista pela categoria). */
function CardCategoria({ titulo, valor, legenda, icone, cor, selecionado, onClick, darkMode, cores }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                p: 2, height: "100%", background: cores.cartao, cursor: onClick ? "pointer" : "default",
                borderLeft: "5px solid", borderLeftColor: corDaBarra(cor, darkMode),
                outline: selecionado ? "2px solid" : "none", outlineColor: corDaBarra(cor, darkMode),
                transition: "transform 0.15s ease-in-out",
                "&:hover": onClick ? { transform: "translateY(-1px)" } : {},
            }}
        >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase" sx={{ color: cores.textoSecundario }}>
                        {titulo}
                    </MDTypography>
                    <MDTypography variant="h3" fontWeight="bold" sx={{ color: corDoTexto(cor, darkMode) }}>
                        {(valor ?? 0).toLocaleString("pt-BR")}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>
                        {legenda}
                    </MDTypography>
                </MDBox>
                <Icon fontSize="large" sx={{ color: corDoTexto(cor, darkMode) }}>{icone}</Icon>
            </MDBox>
        </Card>
    );
}

/** Quadro da conta (base/arquivo antes, total do arquivo e resultado), como na tela anterior. */
function QuadroConta({ titulo, etiqueta, corEtiqueta, valor, unidade, conta, nota, cor, darkMode, cores }) {
    return (
        <MDBox
            sx={{
                p: 2, height: "100%", borderRadius: "10px", border: "1px solid",
                borderColor: cor ? `${cor}.main` : cores.borda,
                bgcolor: (theme) => (cor ? alpha(theme.palette[cor].main, darkMode ? 0.25 : 0.08) : cores.painelEscuro),
            }}
        >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase"
                    sx={{ color: cor ? corDoTexto(cor, darkMode) : cores.textoSecundario }}>
                    {titulo}
                </MDTypography>
                <Chip label={etiqueta} size="small" variant="outlined" color={corEtiqueta || "default"} sx={{ height: 18, fontSize: "0.68rem" }} />
            </MDBox>
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: cor ? corDoTexto(cor, darkMode) : cores.texto, my: 0.5 }}>
                {(valor ?? 0).toLocaleString("pt-BR")}{" "}
                <MDTypography component="span" variant="button" sx={{ color: cores.textoSecundario }}>{unidade}</MDTypography>
            </MDTypography>
            <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>{conta}</MDTypography>
            {nota && <MDTypography variant="caption" display="block" sx={{ color: cores.textoFraco, mt: 0.5 }}>{nota}</MDTypography>}
        </MDBox>
    );
}

export default function ComparativoBase() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);

    const [modelos, setModelos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [carteiras, setCarteiras] = useState([]);
    const [subCarteiras, setSubCarteiras] = useState([]);
    const [modelo, setModelo] = useState(null);
    const [cliente, setCliente] = useState(null);
    const [carteira, setCarteira] = useState(null);
    const [subCarteira, setSubCarteira] = useState(null);
    const [recente, setRecente] = useState(null);
    const [anterior, setAnterior] = useState(null);
    const recenteRef = useRef(null);
    const anteriorRef = useRef(null);

    const [carregando, setCarregando] = useState(false);
    const [exportando, setExportando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [aba, setAba] = useState(0);
    const [filtro, setFiltro] = useState(null);
    const [busca, setBusca] = useState("");
    // Importações já feitas nesta carteira com radiografia (o que a importação fez com cada ficha)
    const [importacoes, setImportacoes] = useState([]);
    const [importacao, setImportacao] = useState(null);

    useEffect(() => {
        post("/remessa/modelos", {}).then((res) => setModelos(Array.isArray(res) ? res : [])).catch(() => null);
        get("/pessoa/list-all").then((res) => setClientes(res?.list || [])).catch(() => null);
        get("/carteira/list-all").then((res) => setCarteiras(res?.list || [])).catch(() => null);
    }, []);

    useEffect(() => {
        setSubCarteira(null);
        setSubCarteiras([]);
        if (!cliente?.value) return;
        get(`/pessoa/by-id/${cliente.value}`).then((res) => setSubCarteiras(res?.subCarteiras || [])).catch(() => null);
    }, [cliente]);

    useEffect(() => {
        setImportacao(null);
        setImportacoes([]);
        if (!cliente?.value || !carteira?.value) return;
        const sub = subCarteira?.value ? `&subCarteiraId=${encodeURIComponent(subCarteira.value)}` : "";
        get(`/relatorios/comparativo-base/importacoes?clienteId=${encodeURIComponent(cliente.value)}&carteiraId=${carteira.value}${sub}`)
            .then((res) => setImportacoes(Array.isArray(res) ? res : []))
            .catch(() => null);
    }, [cliente, carteira, subCarteira]);

    const opcoesModelo = modelos.map((m) => ({ value: m.id, label: m.nome, mappingJson: m.mappingJson }));
    const opcoesCliente = clientes.map((c) => ({ value: c.id, label: c.nomeCompleto || c.nomeComum || "" }));
    const opcoesCarteira = carteiras.map((c) => ({ value: c.id, label: c.nome }));
    const opcoesSub = [{ value: "", label: "Sem subcarteira" }, ...subCarteiras.map((s) => ({ value: s.id, label: s.ccNome }))];

    const nomeModo = (m) => ({ batimento: "Batimento", incremental: "Incremental", devolucao: "Devolução" }[String(m || "").toLowerCase()] || m || "");
    const opcoesImportacao = [
        { value: "", label: "Nenhuma: simular com o arquivo" },
        ...importacoes.map((i) => ({
            value: i.id,
            label: `${i.data} · ${i.arquivo || "arquivo"} · ${nomeModo(i.modo)}${i.usuario ? ` · ${i.usuario}` : ""}${i.status && i.status !== "CONCLUIDO" ? ` (${i.status.toLowerCase()})` : ""}`,
        })),
    ];
    // O arquivo escolhido já foi importado nesta carteira: a simulação compara com a base de depois da importação
    const jaImportado = !importacao && recente ? importacoes.find((i) => i.arquivo === recente.name) : null;

    const podeComparar = importacao ? cliente && carteira : recente && modelo && cliente && carteira;

    const montarForm = () => {
        const fd = new FormData();
        fd.append("recente", recente);
        if (anterior) fd.append("anterior", anterior);
        fd.append("mapping", modelo.mappingJson);
        fd.append("modelo", modelo.label);
        fd.append("clienteId", cliente.value);
        fd.append("carteiraId", carteira.value);
        if (subCarteira?.value) fd.append("subCarteiraId", subCarteira.value);
        return fd;
    };

    const comparar = () => {
        if (!podeComparar) return;
        setCarregando(true);
        const pedido = importacao
            ? get(`/relatorios/comparativo-base/radiografia/${importacao.value}`)
            : postUpload("/relatorios/comparativo-base/processar", montarForm());
        pedido
            .then((res) => {
                setResultado(res);
                setAba(res?.arquivos ? 0 : 1);
                setFiltro(null);
                setBusca("");
            })
            .catch((e) => errorAlert(e?.message || "Erro ao comparar os arquivos."))
            .finally(() => setCarregando(false));
    };

    const baixarPlanilha = () => {
        setExportando(true);
        const pedido = resultado?.importacao
            ? postUploadFile(`/relatorios/comparativo-base/radiografia/${resultado.importacao.id}/exportar`, new FormData(), "RADIOGRAFIA_IMPORTACAO.xlsx")
            : postUploadFile("/relatorios/comparativo-base/exportar", montarForm(), "COMPARATIVO_BASE.xlsx");
        pedido
            .catch((e) => errorAlert(e?.message || "Erro ao gerar a planilha."))
            .finally(() => setExportando(false));
    };

    const trocarArquivo = (setter) => (e) => {
        setter(e.target.files?.[0] || null);
        setResultado(null);
        e.target.value = "";
    };

    const naAbaArquivos = aba === 0;
    const dados = naAbaArquivos ? resultado?.arquivos : resultado?.base;
    const feita = !!resultado?.importacao; // radiografia de uma importação já feita
    const categorias = (naAbaArquivos ? CATEGORIAS_ARQUIVOS : CATEGORIAS_BASE)
        .filter((c) => !c.soComValor || (dados && dados[c.campo] > 0));

    const itensFiltrados = useMemo(() => {
        let itens = dados?.itens || [];
        if (filtro) itens = itens.filter((i) => i.categoria === filtro);
        const termo = busca.trim().toLowerCase();
        if (termo) {
            itens = itens.filter((i) => [i.codigo, i.nome, i.documento, i.ficha]
                .some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(termo)));
        }
        return itens;
    }, [dados, filtro, busca]);

    const rotulo = Object.fromEntries([...CATEGORIAS_ARQUIVOS, ...CATEGORIAS_BASE].map((c) => [c.key, c]));
    const celulaCab = { fontWeight: "bold", fontSize: "0.75rem", backgroundColor: cores.painel, color: cores.texto, whiteSpace: "nowrap", py: 1 };
    const celula = { fontSize: "0.8rem", color: cores.texto, verticalAlign: "top", py: 1 };

    // Cards e quadros com a conta de cada aba (os cards filtram a lista, como as etiquetas)
    let cards = [];
    let quadros = [];
    if (dados && naAbaArquivos) {
        const somaRecente = dados.mantidos + dados.entraram;
        const somaContratos = dados.contratosAnterior - dados.contratosSairam + dados.contratosEntraram;
        cards = [
            { titulo: "Entraram", valor: dados.entraram, legenda: "No arquivo mais recente, não no anterior", icone: "add_circle", cor: "success", filtro: "ENTROU" },
            { titulo: "Saíram", valor: dados.sairam, legenda: "No anterior, não vieram no mais recente", icone: "remove_circle", cor: "error", filtro: "SAIU" },
            {
                titulo: "Mantidos", valor: dados.mantidos, icone: "published_with_changes", cor: "info", filtro: "MANTEVE",
                legenda: dados.mantidosComMudanca > 0
                    ? `Nos dois arquivos (${dados.mantidosComMudanca} com contratos diferentes)`
                    : "Nos dois arquivos, sem mudança nos contratos",
            },
            {
                titulo: "Contratos com outro valor", valor: dados.contratosOutroValor, icone: "price_change", cor: "warning",
                legenda: `Contratos: ${dados.contratosEntraram} novo(s) e ${dados.contratosSairam} que saíram`,
            },
        ];
        quadros = [
            {
                titulo: "Arquivo anterior", etiqueta: "Origem", valor: dados.devedoresAnterior, unidade: "devedores",
                conta: <>{dados.linhasAnterior} linhas · {dados.contratosAnterior} contratos · Saíram: <b>{dados.sairam}</b></>,
            },
            {
                titulo: "Arquivo mais recente", cor: "info", valor: dados.devedoresRecente, unidade: "devedores",
                etiqueta: somaRecente === dados.devedoresRecente ? "Bate 100%" : "Não bate",
                corEtiqueta: somaRecente === dados.devedoresRecente ? "success" : "error",
                conta: <>Mantidos ({dados.mantidos}) + Entraram ({dados.entraram}) = <b>{somaRecente}</b></>,
            },
            {
                titulo: "Contratos no mais recente", cor: "success", valor: dados.contratosRecente, unidade: "contratos",
                etiqueta: somaContratos === dados.contratosRecente ? "Bate 100%" : "Não bate",
                corEtiqueta: somaContratos === dados.contratosRecente ? "success" : "error",
                conta: <>Anterior ({dados.contratosAnterior}) − Saíram ({dados.contratosSairam}) + Entraram ({dados.contratosEntraram}) = <b>{somaContratos}</b></>,
            },
        ];
    } else if (dados) {
        // Remessa = os devedores do arquivo; base final = fichas ativas depois da importação (baseDepois). Na radiografia
        // (importação já feita), o que ela fez com cada ficha e a base contada antes e depois dela
        const totalRemessa = dados.entram + dados.reativados + dados.quitadosVoltam + dados.mantidos + dados.naoReativam + dados.erros;
        const totalDoArquivo = resultado.arquivos ? resultado.arquivos.devedoresRecente : totalRemessa;
        const remessaNaBase = dados.baseDepois - dados.ficamPorAcordo;
        const naoFicam = totalRemessa - remessaNaBase;
        const foraDoBatimento = dados.ativosAntes != null
            ? dados.mantidos - (dados.ativosAntes - dados.saem - dados.ficamPorAcordo) : 0;
        const notas = [];
        if (naoFicam > 0) {
            notas.push(`${naoFicam} da remessa não ${feita ? "ficaram" : "ficam"} na base ativa: fichas que não reativam${dados.erros > 0 ? ", com erro" : ""}${foraDoBatimento > 0 ? " ou fora do batimento (honorários ou outra subcarteira)" : ""}.`);
        }
        if (feita && dados.baseCalculada != null && dados.baseCalculada !== dados.baseDepois) {
            notas.push(`A conta (antes − saíram + novos + reativados + quitados) dá ${dados.baseCalculada}: a diferença mudou na carteira durante a importação.`);
        }
        if (feita && dados.ativosHoje !== dados.baseDepois) {
            notas.push(`Hoje a carteira tem ${dados.ativosHoje.toLocaleString("pt-BR")} fichas ativas (acordos, devoluções e importações depois desta mudam a base).`);
        }
        cards = [
            {
                titulo: "Casos novos", valor: dados.entram, icone: "add_circle", cor: "success", filtro: "ENTRA",
                legenda: `${feita ? "Entraram na carteira com a remessa" : "Vieram na remessa sem ficha na carteira"}${dados.entramDevedorNovo > 0 ? ` (${dados.entramDevedorNovo} devedor(es) novo(s))` : ""}`,
            },
            {
                titulo: "Casos reativados", valor: dados.reativados, icone: "autorenew", cor: "warning", filtro: "REATIVA",
                legenda: feita ? "Devolvidos que voltaram para Cobrança" : "Devolvidos que voltam para Cobrança",
            },
            {
                titulo: "Quitados que voltaram", valor: dados.quitadosVoltam, icone: "replay", cor: "roxo", filtro: "QUITADO_VOLTA",
                legenda: feita ? "Quitados com débito novo, de volta à Cobrança" : "Quitados com débito novo: voltam para Cobrança",
            },
            { titulo: "Mantidos (remessa)", valor: dados.mantidos, legenda: `Ativos que ${feita ? "vieram" : "vêm"} de novo na remessa`, icone: "published_with_changes", cor: "info", filtro: "MANTEM" },
            {
                titulo: "Mantidos (acordo)", valor: dados.ficamPorAcordo, icone: "handshake", cor: "ciano", filtro: "FICA_ACORDO",
                legenda: feita ? "Fora da remessa, ficaram por acordo ativo" : "Fora da remessa, retidos por acordo ativo",
            },
        ];
        quadros = [
            {
                titulo: feita ? "Base antes da importação" : "Base atual no sistema", etiqueta: "Origem",
                valor: dados.ativosAntes, unidade: "casos",
                conta: (
                    <>
                        Devolvidos no batimento ({feita ? "saíram" : "saem"} da base): <b>{dados.saem}</b>
                        {feita && dados.contratosDevolvidos > 0 && <> · Contratos devolvidos: <b>{dados.contratosDevolvidos}</b></>}
                    </>
                ),
            },
            {
                titulo: feita ? "Total da remessa importada" : "Total da remessa nova", cor: "info", valor: totalRemessa, unidade: "casos",
                etiqueta: totalRemessa === totalDoArquivo ? "Bate 100%" : "Não bate",
                corEtiqueta: totalRemessa === totalDoArquivo ? "success" : "error",
                conta: (
                    <>
                        Novos ({dados.entram}) + Reativados ({dados.reativados}) + Quitados ({dados.quitadosVoltam}) + Mantidos ({dados.mantidos})
                        {dados.naoReativam > 0 && <> + Não reativam ({dados.naoReativam})</>}
                        {dados.erros > 0 && <> + Com erro ({dados.erros})</>} = <b>{totalRemessa}</b>
                    </>
                ),
            },
            {
                titulo: feita ? "Base depois da importação" : "Base atualizada final no sistema", etiqueta: "Final ativa",
                corEtiqueta: "success", cor: "success", valor: dados.baseDepois, unidade: "casos",
                conta: <>Remessa nova ({remessaNaBase}) + Mantidos p/ acordo ({dados.ficamPorAcordo}) = <b>{dados.baseDepois}</b></>,
                nota: notas.length ? notas.join(" ") : null,
            },
        ];
    }

    const seletorArquivo = (titulo, arquivo, ref, setter, ajuda) => (
        <Grid item xs={12} md={4}>
            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>{titulo}</InputLabel>
            <MDBox display="flex" alignItems="center" gap={1}>
                <input type="file" ref={ref} style={{ display: "none" }} accept=".xlsx,.xls" onChange={trocarArquivo(setter)} />
                <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38, whiteSpace: "nowrap" }} onClick={() => ref.current?.click()}>
                    <Icon sx={{ mr: 0.5 }}>upload_file</Icon> Escolher arquivo
                </MDButton>
                <MDTypography variant="caption" color="text" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {arquivo ? arquivo.name : "Nenhum arquivo escolhido"}
                </MDTypography>
                {arquivo && (
                    <Tooltip title="Tirar o arquivo">
                        <Icon fontSize="small" sx={{ cursor: "pointer", color: cores.textoFraco }} onClick={() => { setter(null); setResultado(null); }}>close</Icon>
                    </Tooltip>
                )}
            </MDBox>
            <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>{ajuda}</MDTypography>
        </Grid>
    );

    return (
        <MDBox pt={1} pb={3}>
            <Card sx={{ px: 2, py: 1.5, mb: 2, background: cores.cartao }}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={3}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Modelo da remessa</InputLabel>
                        <MDSelect name="modelo" placeholder="Escolha o modelo" options={opcoesModelo} getOptionLabel={(o) => o.label}
                            value={modelo} onChange={(o) => { setModelo(o); setResultado(null); }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
                        <MDSelect name="cliente" placeholder="Escolha o cliente" options={opcoesCliente} getOptionLabel={(o) => o.label}
                            value={cliente} onChange={(o) => { setCliente(o); setResultado(null); }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Carteira</InputLabel>
                        <MDSelect name="carteira" placeholder="Escolha a carteira" options={opcoesCarteira} getOptionLabel={(o) => o.label}
                            value={carteira} onChange={(o) => { setCarteira(o); setResultado(null); }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Subcarteira</InputLabel>
                        <MDSelect name="subCarteira" placeholder={subCarteiras.length ? "Sem subcarteira" : "O cliente não tem subcarteira"} options={opcoesSub}
                            getOptionLabel={(o) => o.label} value={subCarteira} isDisabled={!subCarteiras.length}
                            onChange={(o) => { setSubCarteira(o?.value ? o : null); setResultado(null); }} />
                    </Grid>
                    {seletorArquivo("Arquivo mais recente", recente, recenteRef, setRecente,
                        "A remessa que vai ser importada (Excel). É comparada com a base da carteira e com o arquivo anterior.")}
                    {seletorArquivo("Arquivo anterior (opcional)", anterior, anteriorRef, setAnterior,
                        "A remessa anterior do cliente, para ver o que entrou, saiu e ficou de um arquivo para o outro.")}
                    <Grid item xs={12} md={4}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Importação já feita (radiografia)</InputLabel>
                        <MDSelect name="importacao" placeholder={importacoes.length ? "Nenhuma: simular com o arquivo" : "Nenhuma importação com radiografia"}
                            options={opcoesImportacao} getOptionLabel={(o) => o.label} value={importacao} isDisabled={!importacoes.length}
                            onChange={(o) => { setImportacao(o?.value ? o : null); setResultado(null); }} />
                        <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
                            {!cliente || !carteira
                                ? "Escolha cliente e carteira para ver as importações feitas."
                                : importacoes.length
                                    ? "O que a importação fez com cada ficha: casos novos, reativados, quitados que voltaram, mantidos, por acordo e devolvidos. Não precisa do arquivo."
                                    : "Nenhuma importação com radiografia nesta carteira: só as feitas a partir de agora têm."}
                        </MDTypography>
                    </Grid>
                    {jaImportado && (
                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="warning" display="block" sx={{ px: 1 }}>
                                Este arquivo já foi importado nesta carteira em {jaImportado.data}: a simulação compara com a base de hoje, que já tem
                                a importação. Para ver o que ela fez com cada ficha, escolha a importação em "Importação já feita".
                            </MDTypography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <MDTypography variant="caption" color="text" sx={{ px: 1 }}>
                                {importacao
                                    ? "A radiografia mostra o que a importação gravou para cada ficha. Nada é gravado agora."
                                    : "Os arquivos são lidos pelo modelo, como na importação, e comparados por devedor (Código Cliente). Nada é gravado."}
                            </MDTypography>
                            <MDBox display="flex" gap={1}>
                                {resultado && (
                                    <MDButton variant="outlined" color="info" size="medium" onClick={baixarPlanilha} disabled={exportando}>
                                        {exportando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>download</Icon>}
                                        {exportando ? "Gerando planilha..." : "Baixar planilha"}
                                    </MDButton>
                                )}
                                <MDButton variant="contained" color="info" size="medium" onClick={comparar} disabled={!podeComparar || carregando}>
                                    {carregando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>{importacao ? "biotech" : "compare_arrows"}</Icon>}
                                    {carregando ? (importacao ? "Montando..." : "Comparando...") : importacao ? "Ver radiografia" : "Comparar"}
                                </MDButton>
                            </MDBox>
                        </MDBox>
                    </Grid>
                </Grid>
            </Card>

            {resultado && (
                <>
                <Card sx={{ px: 2, pt: 0.5, pb: 1.5, mb: 2, background: cores.cartao }}>
                    <MDBox sx={{ borderBottom: 1, borderColor: cores.divisoria, mb: 1 }}>
                        <Tabs value={aba} onChange={(e, nova) => { setAba(nova); setFiltro(null); setBusca(""); }}
                            textColor={darkMode ? "inherit" : "primary"} indicatorColor="info">
                            <Tab label="Arquivo × arquivo anterior" disabled={!resultado.arquivos}
                                sx={{ fontSize: 12, color: darkMode ? (aba === 0 ? "#fff" : "rgba(255, 255, 255, 0.7)") : "inherit" }} />
                            <Tab label={feita ? "Radiografia da importação" : "Arquivo × base de dados"}
                                sx={{ fontSize: 12, color: darkMode ? (aba === 1 ? "#fff" : "rgba(255, 255, 255, 0.7)") : "inherit" }} />
                        </Tabs>
                    </MDBox>
                    {!dados ? (
                        <MDTypography variant="button" color="text">Escolha o arquivo anterior para comparar um arquivo com o outro.</MDTypography>
                    ) : (
                        <MDTypography variant="caption" color="text" display="block">
                            {naAbaArquivos
                                ? `${texto(resultado.arquivoAnterior)} (anterior) → ${texto(resultado.arquivoRecente)} (mais recente) · comparados por devedor (Código Cliente)`
                                : feita
                                    ? `${texto(resultado.cliente)} · ${texto(resultado.carteira)} · importação de ${texto(resultado.importacao.data)}`
                                        + ` (${texto(resultado.importacao.arquivo)}, modo ${nomeModo(resultado.importacao.modo)}`
                                        + `${resultado.importacao.usuario ? `, por ${resultado.importacao.usuario}` : ""}`
                                        + `${resultado.importacao.dataRemessa ? `, remessa de ${resultado.importacao.dataRemessa}` : ""})`
                                    : `${texto(resultado.cliente)} · ${texto(resultado.carteira)} · o que a importação no modo Batimento faria com a base de hoje`}
                        </MDTypography>
                    )}
                    {feita && resultado.importacao.status !== "CONCLUIDO" && (
                        <MDTypography variant="caption" color="warning" display="block">
                            A importação não terminou (situação: {texto(resultado.importacao.status)}): a radiografia mostra só o que ela gravou até parar.
                        </MDTypography>
                    )}
                    {feita && String(resultado.importacao.modo || "").toLowerCase() !== "batimento" && (
                        <MDTypography variant="caption" color="text" display="block">
                            Importação sem batimento: nada foi devolvido, e as fichas que não vieram no arquivo continuaram como estavam.
                        </MDTypography>
                    )}
                    {resultado.linhasSemCodigo > 0 && (
                        <MDTypography variant="caption" color="warning" display="block">
                            {resultado.linhasSemCodigo} linha(s) sem Código Cliente no arquivo mais recente: a importação ignora.
                        </MDTypography>
                    )}
                </Card>

                {dados && (
                    <>
                        <MDBox display="grid" gap={2} mb={2} sx={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                            {cards.map((c) => (
                                <CardCategoria key={c.titulo} {...c} darkMode={darkMode} cores={cores} selecionado={!!c.filtro && filtro === c.filtro}
                                    onClick={c.filtro ? () => setFiltro(filtro === c.filtro ? null : c.filtro) : undefined} />
                            ))}
                        </MDBox>
                        <Grid container spacing={2} mb={2} alignItems="stretch">
                            {quadros.map((q) => (
                                <Grid item xs={12} md={4} key={q.titulo}>
                                    <QuadroConta {...q} darkMode={darkMode} cores={cores} />
                                </Grid>
                            ))}
                        </Grid>

                        <Card sx={{ p: 2, background: cores.cartao }}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={1.5}>
                                <MDBox display="flex" flexWrap="wrap" gap={1}>
                                    {categorias.map((c) => (
                                        <Tooltip key={c.key} title={c.dica}>
                                            <Chip
                                                label={`${c.label}: ${dados[c.campo] ?? 0}`}
                                                color={corDaEtiqueta(c.cor)}
                                                variant={filtro === c.key ? "filled" : "outlined"}
                                                onClick={() => setFiltro(filtro === c.key ? null : c.key)}
                                                sx={{ fontWeight: "bold", ...sxDaEtiqueta(c.cor, darkMode, filtro === c.key) }}
                                            />
                                        </Tooltip>
                                    ))}
                                </MDBox>
                                <MDBox width={{ xs: "100%", sm: 320 }}>
                                    <MDInput size="small" fullWidth placeholder="Buscar por código, nome, CPF/CNPJ ou ficha"
                                        value={busca} onChange={(e) => setBusca(e.target.value)} />
                                </MDBox>
                            </MDBox>

                            <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1 }}>
                                <Table size="small" sx={{ minWidth: 1100 }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            <TableCell sx={celulaCab}>Situação</TableCell>
                                            <TableCell sx={celulaCab}>Código Cliente</TableCell>
                                            <TableCell sx={{ ...celulaCab, minWidth: 200 }}>Devedor</TableCell>
                                            <TableCell sx={celulaCab}>CPF/CNPJ</TableCell>
                                            {naAbaArquivos ? (
                                                <>
                                                    <TableCell sx={celulaCab} align="right">Contratos (anterior → recente)</TableCell>
                                                    <TableCell sx={celulaCab} align="right">Valor (anterior → recente)</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell sx={{ ...celulaCab, minWidth: 170 }}>Ficha hoje</TableCell>
                                                    <TableCell sx={celulaCab} align="right">Contratos no arquivo</TableCell>
                                                    <TableCell sx={celulaCab} align="right">Valor no arquivo</TableCell>
                                                </>
                                            )}
                                            <TableCell sx={{ ...celulaCab, minWidth: 300 }}>Observação</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {itensFiltrados.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ ...celula, py: 3 }}>Nenhum devedor neste filtro.</TableCell>
                                            </TableRow>
                                        )}
                                        {itensFiltrados.slice(0, LIMITE_TELA).map((i, idx) => (
                                            <TableRow key={`${i.categoria}-${i.codigo}-${i.ficha}-${idx}`} hover>
                                                <TableCell sx={celula}>
                                                    <Chip size="small" variant="outlined" color={corDaEtiqueta(rotulo[i.categoria]?.cor || "default")}
                                                        label={rotulo[i.categoria]?.label || i.categoria}
                                                        sx={{ height: 20, fontSize: "0.7rem", ...sxDaEtiqueta(rotulo[i.categoria]?.cor, darkMode, false) }} />
                                                </TableCell>
                                                <TableCell sx={celula}>{texto(i.codigo)}</TableCell>
                                                <TableCell sx={celula}>{texto(i.nome)}</TableCell>
                                                <TableCell sx={{ ...celula, whiteSpace: "nowrap" }}>{texto(i.documento)}</TableCell>
                                                {naAbaArquivos ? (
                                                    <>
                                                        <TableCell sx={celula} align="right">{i.contratosAnterior} → {i.contratosRecente}</TableCell>
                                                        <TableCell sx={{ ...celula, whiteSpace: "nowrap" }} align="right">
                                                            {i.valorAnterior != null ? toCurrency(i.valorAnterior) : "-"} → {i.valorRecente != null ? toCurrency(i.valorRecente) : "-"}
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell sx={celula}>
                                                            {i.ficha ? `#${i.ficha} · ${texto(i.situacao)}${i.status ? ` · ${i.status}` : ""}` : "-"}
                                                        </TableCell>
                                                        <TableCell sx={celula} align="right">{i.contratosArquivo || "-"}</TableCell>
                                                        <TableCell sx={{ ...celula, whiteSpace: "nowrap" }} align="right">
                                                            {i.valorArquivo != null && i.contratosArquivo ? toCurrency(i.valorArquivo) : "-"}
                                                        </TableCell>
                                                    </>
                                                )}
                                                <TableCell sx={{ ...celula, color: cores.textoSecundario }}>{texto(i.observacao)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <MDTypography variant="caption" color="text" display="block" mt={1}>
                                {itensFiltrados.length > LIMITE_TELA
                                    ? `Mostrando ${LIMITE_TELA} de ${itensFiltrados.length} devedores: a planilha leva todos.`
                                    : `${itensFiltrados.length} devedor(es).`}
                            </MDTypography>
                        </Card>
                    </>
                )}
                </>
            )}
        </MDBox>
    );
}
